import { useState, useEffect, useRef } from "react";
import { message } from "antd";
import moment from "moment";
import { getRelationId } from "../../../../services/utils";

const useEditCampaign = (campaign, deps) => {
  const {
    history,
    cart,
    categories,
    fetchingDisplaysInfo,
    getCategories,
    clearCampaignData,
    setDefaultContent,
    setToleranceResolution,
    setToleranceResolutionInit,
    removeDisplayFromCart,
    getDisplaysInfoRequest,
    setDates,
    setCampaignName,
    setProgrammatic,
    setBudget,
    updateBundles,
    addCustomContentFromCampaign,
    updateCustomContentRequestActive,
    activateEditCampaign,
    setNewVersionCampaign,
    t,
  } = deps;

  const [loadingEdit, setLoadingEdit] = useState(false);
  const prevFetching = useRef(fetchingDisplaysInfo);

  useEffect(() => {
    if (prevFetching.current && !fetchingDisplaysInfo && loadingEdit) {
      setLoadingEdit(false);
      if (campaign && campaign.new_ssp) {
        history.push("/campaignManager");
        setNewVersionCampaign(true);
      } else {
        history.push("/videoUpload");
        setNewVersionCampaign(false);
      }
    }
    prevFetching.current = fetchingDisplaysInfo;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchingDisplaysInfo]);

  const getDisplaysOfCampaign = (start_date, end_date) => {
    const listDisplaysNotOnCart = [
      ...new Set(campaign.contents_displays.map((d) => d.display_id)),
    ];
    if (listDisplaysNotOnCart.length === 0) {
      message.error(t("There was an error"));
      setLoadingEdit(false);
      return;
    }
    getDisplaysInfoRequest({
      date_from: start_date,
      date_to: end_date,
      currency: campaign.currency,
      invoice_issuing_country: campaign.invoice_issuing.alpha_2_code,
      client_id: campaign.client ? campaign.client.id : null,
      list_id: listDisplaysNotOnCart,
      external_dsp_id: campaign.external_dsp_id,
      smart_campaign_enabled: campaign.programmatic_provider_id ? 1 : null,
      is_campaign: 1,
    });
  };

  const startEdit = () => {
    if (!campaign) return;
    setLoadingEdit(true);

    if (!categories || categories.length === 0) getCategories();
    clearCampaignData();

    let content = [campaign.contents_displays[0]];
    if (
      campaign.contents_displays[0].content_type === "smartv2" &&
      campaign.contents_displays[0].content_child_contents &&
      campaign.contents_displays[0].content_child_contents.length > 0
    ) {
      content = campaign.contents_displays[0].content_child_contents;
    }

    if (!campaign.new_ssp) {
      setDefaultContent({
        file: content[0].content_file || content[0].file,
        file_thumb: content[0].content_file || content[0].file,
        width: content[0].content_width || content[0].width,
        height: content[0].content_height || content[0].height,
        id: content[0].content_id || content[0].id,
        name: content[0].content_name || content[0].name,
        type: content[0].content_type || content[0].type,
        length: content[0].content_length || content[0].length,
        content_version_name: content[0].content_version_name,
        ready: true,
      });
    } else {
      setToleranceResolutionInit(campaign.tolerance_resolution || 0);
      setToleranceResolution(campaign.tolerance_resolution);
    }

    const arrayDisplays = [];
    campaign.contents_displays.forEach((d) => {
      if (!arrayDisplays.includes(d.display_id)) arrayDisplays.push(d.display_id);
    });
    const arrayDisplaysCart = (cart || []).map((c) => c.id);
    arrayDisplaysCart
      .filter((id) => !arrayDisplays.includes(id))
      .forEach((id) => removeDisplayFromCart(id));

    const start_date = campaign.start_date && moment(campaign.start_date).format("YYYY-MM-DD");
    const end_date = campaign.end_date && moment(campaign.end_date).format("YYYY-MM-DD");

    getDisplaysOfCampaign(start_date, end_date);

    setDates(start_date, end_date);
    setCampaignName(campaign.name);
    setProgrammatic(campaign.smart_campaign === 1 ? "programmatic" : "traditional");
    setBudget(parseFloat(campaign.total_price_with_profit));

    campaign.contents_displays.forEach((cd, i) => {
      let contents = [cd];
      if (cd.content_child_contents && cd.content_type === "smartv2") {
        contents = cd.content_child_contents;
      }
      contents.forEach((c, indice) => {
        const promised_shows =
          c.payment_status === "approved"
            ? c.promised_shows - c.promised_shows_subsidized
            : c.promised_shows_original;

        const data = {
          relationId: c.campaign_id ? cd.id : getRelationId(cd.id, indice),
          indice_relation: !c.campaign_id ? indice : null,
          active: cd.active,
          displayId: cd.display_id,
          fill_screen: cd.fill_screen,
          revision_status: cd.revision_status,
          isPendingContent: cd.revision_status === "pending_content",
          promised_shows_subsidized: cd.promised_shows_subsidized,
          promised_shows,
          id: c.content_id ? c.content_id : c.id,
          length: c.length ? c.length : null,
          content: {
            file: c.content_file ? c.content_file : c.file,
            file_thumb: c.content_file ? c.content_file : c.file,
            width: c.content_width ? c.content_width : c.width,
            height: c.content_height ? c.content_height : c.height,
            id: c.content_id ? c.content_id : c.id,
            name: c.content_name ? c.content_name : c.name,
            type: c.content_type ? c.content_type : c.type,
            ready: true,
            content_version_name: c.version_name ? c.version_name : c.content_version_name,
            length: c.content_length ? c.content_length : c.length,
            isPlaceholder: c.revision_status === "pending_content",
          },
          rules: c.rules && c.rules.length > 0 ? JSON.parse(c.rules) : [],
          cpm: cd.cpm ? cd.cpm : 0,
          programmatic_volume_discount_apply: cd.programmatic_volume_discount_apply,
          programmatic_volume_discounts: cd.programmatic_volume_discounts,
          promised_shows_discounted: cd.promised_shows_discounted,
          is_secured_investment: cd.is_secured_investment,
        };

        updateBundles(campaign.bundles);
        const subsidized_price =
          cd.paid_amount_with_profit == "0.00" && cd.promised_shows > 0;

        addCustomContentFromCampaign(
          data.relationId,
          data.indice_relation,
          data.active,
          data.displayId,
          data.fill_screen,
          data.revision_status,
          data.isPendingContent,
          false,
          data.promised_shows,
          data.length,
          data.content,
          data.rules,
          subsidized_price,
          data.promised_shows_subsidized,
          data.content.name,
          data.cpm,
          data.programmatic_volume_discount_apply,
          data.programmatic_volume_discounts,
          data.promised_shows_discounted,
          data.is_secured_investment
        );
        if (!campaign.new_ssp) {
          updateCustomContentRequestActive(data.content.id, data.displayId, data.relationId, data.active);
        }
      });
    });

    activateEditCampaign(campaign);
  };

  return { startEdit, loadingEdit };
};

export default useEditCampaign;
